import { CommonModule } from "@angular/common";
import {Component, computed, Input, OnChanges, OnInit, signal, inject, input, ChangeDetectionStrategy} from "@angular/core";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import {
  LorenMeteringType,
  LorenMeteringTypeDefaultDescriptions,
  LorenMeteringTypeTranslation,
} from "../../../../features/loren-assets/interfaces/loren-metering-type";
import { LOREN_ROUTES } from "../../../constants/loren-routes.constants";
import { LorenMetricUnits } from "../constants/loren-metric-units.constants";
import { LorenMetricCategories, LorenMetricKinds } from "../constants/loren-metric-types.constants";
import { LorenNumberModule } from "../../../pipes/loren-number/loren-number.module";
import { LorenDateModule } from "../../../pipes/loren-date/loren-date.module";
import { BatteryLevelComponent } from "../../loren-battery-level/components/battery-level/battery-level";
import {
  LorenDashboardCardMeteringView
} from "../../loren-dashboard-card/interfaces/loren-dashboard-card-metering-view.interface";
import { LorenBaseMapModule } from "../../loren-base-map/loren-base-map.module";
import { LorenImageModule } from "../../../pipes/loren-image/loren-image.module";
import { LorenMeteringDataIdsEnum } from "../../../../features/loren-assets/enums/loren-metering-data-ids.enum";
import { LorenMetricBinaryValues, LorenMetricIcons, LorenMetricIconsBinary } from "../constants/loren-metric-icons";
import { first } from "rxjs";
import { LorenAssetService } from "../../../../features/loren-assets/services/loren-asset-service/loren-asset.service";
import {
  LorenAssetUtilsService
} from "../../../../features/loren-assets/services/loren-asset-utils-service/loren-asset-utils.service";
import { formatNumericValue, getPrimaryMeteringValue } from "../../../../features/asset/metric.utils";
import {
  getFillLevelTranslationKeyFromData,
  getFillLevelValueFromData,
  isFillLevelSensor,
} from "../../../../features/asset/fill-level.utils";
import {MeticConfig} from "../../../../features/metric/metric";
import {LorenAsset} from "../../../../features/loren-assets/interfaces/loren-asset.interface";
import {LorenMetering} from "../../../../features/loren-assets/interfaces/loren-metering.interface";
import {
  LorenMeteringContext,
  METERING_CONTEXT_TRANSLATION_NAMES,
} from "../../../../features/loren-assets/enums/loren-metering-context.enum";

@Component({
  selector: "loren-metric",
  imports: [
    CommonModule,
    MatIcon,
    MatTooltip,
    TranslocoPipe,
    LorenNumberModule,
    LorenDateModule,
    BatteryLevelComponent,
    LorenBaseMapModule,
    LorenImageModule,
  ],
  templateUrl: "./loren-metric.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-metric.component.scss",
})
export class LorenMetricComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private assetService = inject(LorenAssetService);

  @Input({required: true}) assetId: number;
  @Input() avatar: string;
  @Input() isTableView: boolean;
  @Input({required: true}) metric: LorenDashboardCardMeteringView;

  assetRaw = input.required<LorenAsset>();
  metricRaw = input.required<LorenMetering>();

  readonly metricCategories = LorenMetricCategories;
  readonly metricTypes = LorenMeteringType;
  private hostName = signal<string | null>(null);

  defaultDescription = signal<string>("");
  hasGPSCovering = signal<boolean>(false);
  icon = signal<string>("");
  kind = signal<LorenMetricCategories>(null);
  time = signal(null);
  title = signal<string>("");
  unit = signal<string>("");
  value = signal(null);
  valueLabel = computed(() => {
    // This is a proxy for the value while migrating
    // value should become a scalar type or use a dedicated component
    switch (this.kind()) {
      case LorenMetricCategories.BINARY:
        return this.translocoService.translate(
          LorenMetricBinaryValues.get(this.getBinaryPresentationType())[this.value()]
        );
      default:
        return this.value();
    }
  });

  iconActive = computed<boolean>(() => {
    switch (this.kind()) {
      case LorenMetricCategories.BINARY:
        return this.value();
      default:
        return false;
    }
  });

  ngOnInit() {
    this.getData();
  }

  ngOnChanges() {
    this.getData();
  }

  handleHistoryClick() {
    this.router.navigateByUrl(`/${LOREN_ROUTES.ASSET}/${this.assetId}#diagrams`, {
      state: {
        selectedMeteringId: this.metric.meteringId,
      },
    });
  }

  navigateToMarker() {
    this.router.navigateByUrl(LOREN_ROUTES.MAP, {
      state: { assetId: this.assetId },
    });
  }

  private getData() {
    const type = this.metric.type;
    const translationKey = this.metric.meteringContext === LorenMeteringContext.STATE
      ? METERING_CONTEXT_TRANSLATION_NAMES.get(LorenMeteringContext.STATE)
      : LorenMeteringTypeTranslation.get(type);
    const value = isFillLevelSensor(type)
      ? getFillLevelValueFromData(this.metric.data)
      : type === LorenMeteringType.NTL_SENSOR
        ? getPrimaryMeteringValue(this.metricRaw())
        : this.metric.data[0]?.value;
    this.kind.set(LorenMetricKinds.get(type));
    this.time.set(isNaN(this.metric.time.getDate()) ? null : this.metric.time);
    this.title.set(
      this.translocoService.translate(translationKey)
    );
    this.defaultDescription.set(
      this.translocoService.translate(
        this.metric.meteringContext === LorenMeteringContext.STATE
          ? translationKey
          : LorenMeteringTypeDefaultDescriptions.get(type)
      )
    );
    this.value.set(this.getValue(value));
    this.unit.set(
      type === LorenMeteringType.TEMPERATURE_SENSOR
        ? LorenMetricUnits.get(type).split(" ")[1]
        : LorenMetricUnits.get(type)
    );
    this.icon.set(
      this.kind() === LorenMetricCategories.BINARY
        ? LorenMetricIconsBinary.get(this.getBinaryPresentationType())[
            LorenAssetUtilsService.getBinaryValue(
              this.metric.type,
              value,
              this.metric.meteringContext
            )
          ]
        : LorenMetricIcons.get(type)
    );
    if (this.kind() === LorenMetricCategories.TRACKING) {
      this.getGPSCovering();
    }
  }

  private getBinaryPresentationType(): LorenMeteringType | LorenMeteringContext {
    return this.metric.meteringContext === LorenMeteringContext.STATE
      ? LorenMeteringContext.STATE
      : this.metric.type;
  }

  private getValue(value) {
    const noValue = value === null || value === undefined;

    switch (this.kind()) {
      case LorenMetricCategories.IMAGE:
        return value;
      case LorenMetricCategories.BINARY:
        return noValue
          ? null
          : LorenAssetUtilsService.getBinaryValue(
            this.metric.type,
            value,
            this.metric.meteringContext
          ) === 'true';
      case LorenMetricCategories.NUMERIC:
        if (isFillLevelSensor(this.metric.type)) {
          const translationKey = getFillLevelTranslationKeyFromData(
            this.metric.data
          );
          return translationKey
            ? this.translocoService.translate(translationKey)
            : value;
        }
        return noValue ? null : formatNumericValue(value);
      case LorenMetricCategories.PERCENTAGE:
        if(noValue) {
          return '';
        }

        return (Number(value) * 100).toFixed(2);
      case LorenMetricCategories.PRESENCE_SENSOR:
        return noValue ? null : `${value?.length || 0}`;
      case LorenMetricCategories.PRESENCE_BEACON:
        this.getHostAsset(value);
        return this.hostName()
          ? `${this.translocoService.translate("asset.in")} ${this.hostName()}`
          : this.translocoService.translate("asset.out_of_range");
      case LorenMetricCategories.TRACKING:
        return noValue
          ? null
          : this.isTableView
            ? [
              Number(this.metric.data.find((d) => d.id === "lat")?.value),
              Number(this.metric.data.find((d) => d.id === "lng")?.value),
            ].join(", ")
            : {
              lat: Number(this.metric.data.find((d) => d.id === "lat")?.value),
              lng: Number(this.metric.data.find((d) => d.id === "lng")?.value),
            };
      default:
        return null;
    }
  }

  private getGPSCovering() {
    const gpsCoverageMetering = this.metric.data.find(
      (x) => x.id === LorenMeteringDataIdsEnum.GPS_DATA_RELIABLE
    );
    if (!gpsCoverageMetering) {
      // In case when device does not support satellite metrics
      this.hasGPSCovering.set(true);
      this.checkLocationValidityPeriod();
      return;
    }

    this.hasGPSCovering.set(gpsCoverageMetering?.value === "true");
    this.checkLocationValidityPeriod();
  }

  private checkLocationValidityPeriod() {
    if (this.metric.locationValidityPeriod && this.metric.time) {
      const now = new Date();
      const diff = (now.getTime() - this.metric.time.getTime()) / 1000;
      if (diff >= this.metric.locationValidityPeriod) {
        this.hasGPSCovering.set(false);
      }
    }
  }

  private getHostAsset(id) {
    return this.assetService.lastCachedAssets$
      .pipe(first())
      .subscribe((allAssets) => {
        this.hostName.set(
          id ? allAssets?.find((x) => x.id === Number(id)).name : null
        );
      });
  }

  protected readonly MeticConfig = MeticConfig;
}
